import { Injectable, inject } from '@angular/core';

import { environment } from 'projects/semeris/src/environments/environment';
import { EMPTY } from 'rxjs';
import { catchError, finalize, timeout } from 'rxjs/operators';

import { type DeploymentVariant, BUILD_VARIANT, otherVariant } from './deployment-variant';
import { ActivityEvent } from '../../../home/models/activity-event.model';
import { ActivityService } from '../../../home/services/activity.service';

// Hostname, never `environment.envId` — the `generated` serve config sets envId to the chosen backend.
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

const SWITCH_ACTIVITY_TIMEOUT_MS = 2000;

/**
 * Moves between a stage's classic and `next` frontends. Both serve the same backend, so a
 * switch is a frontend swap at the same path — not an environment change.
 */
@Injectable({ providedIn: 'root' })
export class DeploymentSwitchService {
  private readonly activityService = inject(ActivityService);

  private isLeaving = false;

  /** Which frontend this build is. Correct on localhost, where the hostname isn't. */
  public readonly variant: DeploymentVariant = BUILD_VARIANT;

  /** A dev machine serves neither frontend: the toggle renders to show your side, but cannot navigate. */
  public readonly isLocal: boolean = LOCAL_HOSTNAMES.has(window.location.hostname);

  /** Whether the current HOST serves a `next` deployment — for gating; never use `variant` for that. */
  public readonly isNextHost: boolean = !!environment.nextHost && window.location.hostname === environment.nextHost;

  /**
   * True when this host is one of the stage's two frontends. False on a dev machine and on
   * blue/green hosts, which serve a build but aren't a switch target.
   */
  public readonly isPaired: boolean = !!this.targetHost() && this.isStageHost();

  /**
   * The other frontend at the CURRENT path. Recomputed per call: in-app routing changes the path
   * long after this singleton is constructed, so a cached URL would send you to a stale route.
   */
  public peerUrl(): string | null {
    const { pathname, search, hash } = window.location;

    return this.peerUrlForPath(`${pathname}${search}${hash}`);
  }

  /**
   * The other frontend at an explicit path, for deep-linking a feature that only exists there.
   * `path` is used verbatim, so it may carry its own query string and fragment.
   */
  public peerUrlForPath(path: string): string | null {
    const host = this.targetHost();

    if (!host || !this.isStageHost()) {
      return null;
    }

    return `https://${host}${path.startsWith('/') ? path : `/${path}`}`;
  }

  /** Loads the other frontend at the current path. No-op when already there, or with no counterpart. */
  public switchTo(variant: DeploymentVariant): void {
    const url = this.peerUrl();

    if (!url || variant === this.variant) {
      return;
    }

    this.leaveTo(url);
  }

  /** Loads the other frontend at an explicit path. No-op when there is no counterpart. */
  public openPeerPath(path: string): void {
    const url = this.peerUrlForPath(path);

    if (!url) {
      return;
    }

    this.leaveTo(url);
  }

  /** Records the switch, then navigates — a slow or failed report never blocks the switch. */
  private leaveTo(url: string): void {
    if (this.isLeaving) {
      return;
    }
    this.isLeaving = true;

    // Navigating unloads the page and cancels the request, so it must settle first.
    this.activityService
      .addActivity(ActivityEvent.UserInterfaceSwitch, {
        from: this.variant,
        to: otherVariant(this.variant),
        path: new URL(url).pathname,
      })
      .pipe(
        timeout(SWITCH_ACTIVITY_TIMEOUT_MS),
        catchError(() => EMPTY),
        finalize(() => {
          window.location.assign(url);
          this.isLeaving = false;
        }),
      )
      .subscribe();
  }

  private targetHost(): string | undefined {
    return otherVariant(this.variant) === 'next' ? environment.nextHost : environment.classicHost;
  }

  private isStageHost(): boolean {
    const { hostname } = window.location;

    return hostname === environment.classicHost || hostname === environment.nextHost;
  }
}
